import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Compass, Play, X } from 'lucide-react';
import { useWalkthrough, useDrawer } from '@/app/providers';
import type { WalkthroughPathId } from '@/shared/types';
import { stepsForPath, WALKTHROUGH_PATHS } from '@/shared/walkthrough-steps';

// Walkthrough (v2) — bottom-right compact chip.
//
// Rest state: a small chip showing the step indicator ("1/14") and a
// next-step chevron. Hovering or focusing the chip expands a popover
// upward with the full caption, prev/next, free-explore toggle, and
// dismiss. This replaces the v1 full-width bottom pill which dominated
// the viewport and obscured the caption-vs-controls hierarchy.
//
// Behaviour preserved from v1:
//   - prev/next advance the step counter and navigate to STEPS[next].hash
//   - dismiss → state.dismissed = true, swaps the chip for a resume chip
//     in the same bottom-right slot
//   - free explore → caption switches to "Free explore — paused at step N"
//     and prev/next become hidden (the user is off-spine)
//
// Gate (2026-05-17 audit D1): the walkthrough is sales-demo-only. Hidden
// by default; rendered when `?demo=true`, `?demo=money-path`,
// `?demo=learning-loop`, or `?demo=komatsu` (the four-beat Komatsu run) is
// present in the URL.
//
// Hook (2026-05-17 audit D2): use `useDemoPath()` to gate any
// walkthrough-adjacent effect (e.g. `useWalkthroughSync`) so persona
// auto-flip + indicator sync don't run for default users.
export function useDemoPath(): WalkthroughPathId | null {
  const [params] = useSearchParams();
  const demo = params.get('demo');
  if (demo === 'true' || demo === 'money-path') return 'money-path';
  if (demo === 'learning-loop') return 'learning-loop';
  if (demo === 'komatsu') return 'komatsu';
  return null;
}

/** True when any named walkthrough path is active in the URL. */
export function useIsDemoMode(): boolean {
  return useDemoPath() !== null;
}

function scrollToSceneAnchor(anchorId: string) {
  const el = document.getElementById(anchorId);
  if (!el) return;
  let parent = el.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      parent.scrollHeight > parent.clientHeight
    ) {
      const parentRect = parent.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      parent.scrollTop += elRect.top - parentRect.top - 16;
      return;
    }
    parent = parent.parentElement;
  }
  el.scrollIntoView({ block: 'start' });
}

function withDemoParam(hash: string, demoParam: string | null): string {
  if (!demoParam || demoParam === 'true') return hash;
  const q = hash.indexOf('?');
  const pathname = q >= 0 ? hash.slice(0, q) : hash;
  const params = new URLSearchParams(q >= 0 ? hash.slice(q + 1) : '');
  params.set('demo', demoParam);
  return `${pathname}?${params.toString()}`;
}

export function Walkthrough() {
  const demoPath = useDemoPath();
  const [searchParams] = useSearchParams();
  const demoParam = searchParams.get('demo');
  const {
    pathId,
    currentStep,
    freeExplore,
    dismissed,
    next,
    prev,
    toggleFreeExplore,
    dismiss,
    resume,
  } = useWalkthrough();
  const { open: openDrawer, closeAll } = useDrawer();

  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const activePathId = demoPath ?? pathId;
  const steps = stepsForPath(activePathId);
  const pathTitle = WALKTHROUGH_PATHS[activePathId].title;
  const total = steps.length;

  // Hover close runs on a short grace timer so a diagonal exit that briefly
  // leaves the wrapper (chip → popover corner) doesn't yank the popover shut.
  // The in-gap path itself is covered by the .walk-chip-popover::after bridge.
  const closeTimerRef = useRef<number | null>(null);
  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const openNow = () => {
    cancelClose();
    setOpen(true);
  };
  const closeSoon = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 200);
  };
  useEffect(() => cancelClose, []);

  // Navigate only when the step counter itself advances (prev/next). URL
  // changes alone must NOT pull the user back — clicking off-spine should
  // auto-flip Free Explore (handled in useWalkthroughSync), not yank.
  //
  // 2026-05-17 audit D2 fix: location.pathname / location.search are read
  // inside the effect via a ref but NOT in the dep array. Watching them
  // re-fired the effect on every navigation, which is the root of the
  // observed bounce-to-/dashboard pattern (deep-links, drawer CTAs, outline
  // clicks all triggered this effect). Now the effect only runs when
  // currentStep itself changes via prev/next.
  const locationRef = useRef(location);
  locationRef.current = location;
  const prevStepRef = useRef(currentStep);
  useEffect(() => {
    if (dismissed || freeExplore) {
      prevStepRef.current = currentStep;
      return;
    }
    if (currentStep === prevStepRef.current) return;
    prevStepRef.current = currentStep;
    const target = withDemoParam(steps[currentStep]?.hash ?? '', demoParam);
    if (!target) return;
    const loc = locationRef.current;
    const here = loc.pathname + (loc.search ?? '');
    if (here !== target) navigate(target);
  }, [currentStep, demoParam, freeExplore, dismissed, navigate, steps]);

  const prevFreeExploreRef = useRef(freeExplore);
  useEffect(() => {
    if (dismissed) return;
    const wasFree = prevFreeExploreRef.current;
    prevFreeExploreRef.current = freeExplore;
    if (!wasFree || freeExplore) return;
    const target = withDemoParam(steps[currentStep]?.hash ?? '', demoParam);
    if (!target) return;
    const here = location.pathname + (location.search ?? '');
    if (here !== target) navigate(target);
  }, [freeExplore, dismissed, currentStep, demoParam, location, navigate, steps]);

  const sceneScrollKeyRef = useRef('');
  useEffect(() => {
    if (!demoPath || dismissed) return;
    const scrollId = steps[currentStep]?.scrollTo;
    if (!scrollId) return;
    const key = `${location.pathname}${location.search}|${scrollId}`;
    if (sceneScrollKeyRef.current === key) return;
    sceneScrollKeyRef.current = key;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollToSceneAnchor(scrollId);
      });
    });
  }, [demoPath, currentStep, dismissed, steps, location.pathname, location.search]);

  // Open or close the step drawer so a prior case drawer does not leak.
  const drawerStepRef = useRef(-1);
  useEffect(() => {
    if (!demoPath || dismissed || freeExplore) return;
    if (drawerStepRef.current === currentStep) return;
    drawerStepRef.current = currentStep;
    const step = steps[currentStep];
    if (!step) return;
    const drawer = step.drawer;
    if (drawer?.kind === 'case-drawer') {
      openDrawer('case-drawer', {
        wide: true,
        data: { caseId: drawer.data.caseId },
      });
      const sectionId = drawer.data.section;
      if (sectionId) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            scrollToSceneAnchor(sectionId);
          });
        });
      }
    } else {
      closeAll();
    }
  }, [demoPath, currentStep, dismissed, freeExplore, openDrawer, closeAll, steps]);

  // Close the popover when clicking outside or pressing Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // 2026-05-17 audit D1 gate: walkthrough is sales-demo-only.
  if (!demoPath) return null;

  if (dismissed) {
    return (
      <button
        type="button"
        data-comment-anchor="Walkthrough"
        data-review-mode="true"
        className="walk-chip-resume glass-strip"
        onClick={resume}
        aria-label="Resume walkthrough"
      >
        <Play size={12} aria-hidden="true" />
        <span>Resume</span>
      </button>
    );
  }

  const step = steps[currentStep];
  const stepN = currentStep + 1;
  const caption = freeExplore
    ? `Free explore — paused at step ${stepN}.`
    : step?.caption ?? '';

  const tagClass = ['walk-chip-tag', freeExplore ? 'paused' : ''].filter(Boolean).join(' ');

  return (
    <div
      ref={wrapRef}
      data-comment-anchor="Walkthrough"
      data-review-mode="true"
      className={['walk-chip-wrap', open ? 'open' : ''].filter(Boolean).join(' ')}
      role="status"
      aria-live="polite"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      <button
        type="button"
        className="walk-chip glass-strip"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Walkthrough — step ${stepN} of ${total}`}
      >
        <span className={tagClass}>
          {stepN}/{total}
        </span>
        {!freeExplore && (
          <ChevronRight size={14} aria-hidden="true" className="walk-chip-hint" />
        )}
        {freeExplore && (
          <Compass size={12} aria-hidden="true" className="walk-chip-hint" />
        )}
      </button>

      <div className="walk-chip-popover glass-strip" role="dialog" aria-label="Walkthrough controls">
        <div className="walk-chip-path">{pathTitle}</div>
        <div className="walk-chip-caption">{caption}</div>
        <div className="walk-chip-row">
          <button
            type="button"
            className="walk-chip-nav"
            onClick={prev}
            disabled={currentStep === 0 || freeExplore}
            aria-label="Previous step"
          >
            <ChevronLeft size={14} aria-hidden="true" />
          </button>
          <span className="walk-chip-counter">
            {stepN} / {total}
          </span>
          <button
            type="button"
            className="walk-chip-nav"
            onClick={next}
            disabled={currentStep === total - 1 || freeExplore}
            aria-label="Next step"
          >
            <ChevronRight size={14} aria-hidden="true" />
          </button>
          <span className="walk-chip-divider" />
          <button
            type="button"
            className={['walk-chip-free', freeExplore ? 'on' : ''].filter(Boolean).join(' ')}
            onClick={toggleFreeExplore}
            aria-pressed={freeExplore}
          >
            <Compass size={12} aria-hidden="true" />
            <span>Free explore</span>
          </button>
          <button
            type="button"
            className="walk-chip-dismiss"
            onClick={dismiss}
            aria-label="Dismiss walkthrough"
          >
            <X size={12} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
